import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Snackbar,
  Alert,
  Chip,
  TableSortLabel,
  TablePagination,
  Box,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

export default function HistoryTable({ history, onModify, onDuplicate, onDelete }) {
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [newName, setNewName] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleModify = (project) => {
    onModify(project);
  };

  const handleDelete = (project) => {
    setSelectedProject(project);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDeleted = () => {
    onDelete(selectedProject.id);
    setOpenDeleteDialog(false);
    setSnackbarMessage(`Project "${selectedProject.name}" deleted`);
    setShowSnackbar(true);
  };

  const handleDuplicate = (project) => {
    setSelectedProject(project);
    setOpenDialog(true);
  };

  const handleConfirmDuplicate = () => {
    onDuplicate(selectedProject, newName);
    setOpenDialog(false);
    setSnackbarMessage(`Project "${selectedProject.name}" duplicated`);
    setShowSnackbar(true);
    setNewName("");
  };

  const handleSort = () => {
    setOrder(order === "asc" ? "desc" : "asc");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isNameDuplicate = (name) => {
    return history.some(
      (project) =>
        project.name.trim().toLowerCase() === name.trim().toLowerCase()
    );
  };  

  const sortedHistory = [...history].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return order === "asc" ? dateA - dateB : dateB - dateA;
  });

  const paginatedHistory = sortedHistory.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 10 }}>
        <Table sx={{ minWidth: 1200 }} aria-label="history table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "33%" }}>Project's Name</TableCell>
              <TableCell sx={{ width: "33%" }}>
                <TableSortLabel
                  active={true}
                  direction={order}
                  onClick={handleSort}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ width: "34%" }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedHistory.map((project) => (
              <TableRow key={project.id}>
                <TableCell>{project.name}</TableCell>
                <TableCell>
                  {project.date
                    ? new Date(project.date).toLocaleString("en-GB")
                    : ""}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Chip
                      onClick={() => handleModify(project)}
                      label = "Modify"
                      icon={<EditIcon fontSize="small" />}
                      sx={{
                        borderColor: "grey.400",
                        color: "grey.700",
                        "&:hover": { backgroundColor: "grey.100" },
                      }}
                    />
                    <Chip
                      onClick={() => handleDuplicate(project)}
                      label = "Copy"
                      icon={<ContentCopyIcon fontSize="small" />}
                      sx={{
                        borderColor: "grey.400",
                        color: "grey.700",
                        "&:hover": { backgroundColor: "grey.100" },
                      }}
                    />
                    <Chip
                      onClick={() => handleDelete(project)}
                      icon={<DeleteOutlineIcon fontSize="small" />}
                      sx={{
                        borderColor: "grey.400",
                        color: "grey.700",
                        "&:hover": { backgroundColor: "grey.100" },
                      }}
                    />
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <TablePagination
            component="div"
            count={history.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 100]}
          />
        </Box>
      </TableContainer>

      {/* Duplicate Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Duplicate Project</DialogTitle>
        <DialogContent>
        <TextField
            autoFocus
            margin="dense"
            label="New project's name"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            error={newName !== "" && isNameDuplicate(newName)}
            helperText={
              newName !== "" && isNameDuplicate(newName)
                ? "A project with this name already exists."
                : " " // espace pour garder la même hauteur
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDuplicate}
            disabled={!newName || isNameDuplicate(newName)}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the project "
          {selectedProject ? selectedProject.name : ""}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmDeleted} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
